import { Avatar } from "@ui/Avatar";
import { Button } from "@ui/Button";
import { VStack } from "@ui/Stack";
import { Text } from "@ui/typography/Text";
import { createEffect, createResource, createSignal, Show } from "solid-js";
import { getMyAskPageSubscriptions } from "@/client";
import {
	askPageCreatorFirstName,
	askPageSubscriptionMetaLine,
	askPageUnsubscribeConfirmBody,
	askPageUnsubscribeIntroBody,
} from "../askPageSubscriptionCopy";
import type { AskPagePrice } from "../pageVars";
import "./AskPageUnsubscribePanel.css";

type Props = {
	isOpen: () => boolean;
	onClose: () => void;
	creatorName: string;
	creatorAvatarUrl: string | null;
	isPaidAskPage: boolean;
	entitlementType: AskPagePrice["type"] | null;
	influencerHandle: string;
	onConfirm: () => void | Promise<void>;
	busy?: () => boolean;
	error?: () => string | null;
};

const normalizeHandle = (handle: string) =>
	handle.trim().replace(/^@/, "").toLowerCase();

export const AskPageUnsubscribePanel = (props: Props) => {
	const [step, setStep] = createSignal<1 | 2>(1);
	const firstName = () => askPageCreatorFirstName(props.creatorName);

	const [subscription] = createResource(
		() => (props.isOpen() ? normalizeHandle(props.influencerHandle) : null),
		async (handle) => {
			if (!handle) {
				return null;
			}
			const list = await getMyAskPageSubscriptions({
				query: { page_size: 100, status: "active" },
			});
			return (
				(list.data?.subscriptions ?? []).find(
					(row) => row.influencer.handle?.toLowerCase() === handle,
				) ?? null
			);
		},
	);

	const accessUntil = () => subscription()?.renewal?.current_period_end ?? null;

	const metaLine = () =>
		askPageSubscriptionMetaLine({
			isPaid: props.isPaidAskPage,
			entitlementType: props.entitlementType,
			currentPeriodEnd: subscription()?.renewal?.current_period_end ?? null,
		});

	const reset = () => {
		setStep(1);
	};

	createEffect(() => {
		if (!props.isOpen()) {
			reset();
		}
	});

	const handleClose = () => {
		if (props.busy?.()) {
			return;
		}
		reset();
		props.onClose();
	};

	const handleConfirm = () => {
		if (props.busy?.()) {
			return;
		}
		void props.onConfirm();
	};

	return (
		<div class="ask-page-unsubscribe-panel">
			<Show
				when={step() === 1}
				fallback={
					<VStack spacing="large">
						<Text size="standard" weight="semi-bold" color="ink">
							Unsubscribe from {firstName()}?
						</Text>
						<Text size="small" color="fg2">
							{askPageUnsubscribeConfirmBody({
								firstName: firstName(),
								accessUntil: accessUntil(),
								isPaid: props.isPaidAskPage,
							})}
						</Text>
						<Show when={props.error?.()}>
							<Text size="small" color="error">
								{props.error?.()}
							</Text>
						</Show>
						<div class="ask-page-unsubscribe-panel__actions">
							<Button
								type="button"
								variant="tertiary"
								fullWidth
								disabled={props.busy?.()}
								onClick={handleClose}
							>
								Keep subscription
							</Button>
							<Button
								type="button"
								variant="primary"
								theme="danger"
								fullWidth
								disabled={props.busy?.()}
								onClick={handleConfirm}
							>
								Unsubscribe
							</Button>
						</div>
					</VStack>
				}
			>
				<VStack spacing="large">
					<div class="ask-page-unsubscribe-panel__header">
						<Avatar
							alt={props.creatorName}
							src={props.creatorAvatarUrl ?? undefined}
							size="medium"
						/>
						<VStack spacing="small">
							<Text size="small" weight="semi-bold" color="ink">
								{props.creatorName}
							</Text>
							<Text family="mono" size="xsmall" color="fg3">
								{metaLine()}
							</Text>
						</VStack>
					</div>
					<Text size="small" color="fg2">
						{askPageUnsubscribeIntroBody(
							props.creatorName,
							props.isPaidAskPage,
						)}
					</Text>
					<Button
						type="button"
						variant="secondary"
						theme="danger"
						symbol="person_remove"
						fullWidth
						onClick={() => setStep(2)}
					>
						Unsubscribe
					</Button>
				</VStack>
			</Show>
		</div>
	);
};
